/**
 * The change list of the ACL editor and the Propagate root preview in sentences (UI/UX fix round A, reviews U28 / A17):
 * "Other: gains Read (was no access) — access widens." instead of "changed: other: read (was ---) / effective other:
 * read (was ---; widened) — widened". An entry whose effective access changes exactly as its own bits do gets ONE line
 * (the mask does not limit it); an entry the mask limits keeps a separate "effective access" line.
 */
import { describeAclPrincipal, diffAcl, diffEffective } from '../../../../core/engines/azureStorageAclEffective';
import type { StorageAclEntry, StorageAclPermissions } from '../../../../types/azureStorage';

export type AclChangeSide = { owner?: string; group?: string; entries: StorageAclEntry[] };
export type AclChangeSentence = { text: string; widened?: boolean };

const NONE: StorageAclPermissions = { read: false, write: false, execute: false };
const BITS: ReadonlyArray<[keyof StorageAclPermissions, string]> = [['read', 'Read'], ['write', 'Write'], ['execute', 'Execute']];
const same = (a: StorageAclPermissions, b: StorageAclPermissions): boolean => a.read === b.read && a.write === b.write && a.execute === b.execute;
const keyOf = (e: Pick<StorageAclEntry, 'scope' | 'type' | 'id'>): string => `${e.scope}|${e.type}|${e.id ?? ''}`;
const listed = (words: string[]): string => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);

/** "Read, Execute" / "no access". */
export const permissionWords = (p: StorageAclPermissions): string => BITS.filter(([bit]) => p[bit]).map(([, word]) => word).join(', ') || 'no access';

/** "gains Write, loses Execute". */
function changeClause(before: StorageAclPermissions, after: StorageAclPermissions): string {
  const gains = BITS.filter(([bit]) => !before[bit] && after[bit]).map(([, word]) => word);
  const loses = BITS.filter(([bit]) => before[bit] && !after[bit]).map(([, word]) => word);
  return [gains.length > 0 ? `gains ${listed(gains)}` : '', loses.length > 0 ? `loses ${listed(loses)}` : ''].filter(Boolean).join(', ');
}

function direction(before: StorageAclPermissions, after: StorageAclPermissions): { suffix: string; widened: boolean } {
  const gained = BITS.some(([bit]) => !before[bit] && after[bit]);
  const lost = BITS.some(([bit]) => before[bit] && !after[bit]);
  if (gained && lost) return { suffix: ' — access widens in part.', widened: true };
  return gained ? { suffix: ' — access widens.', widened: true } : { suffix: ' — access narrows.', widened: false };
}

function subject(entry: StorageAclEntry, names: Record<string, string>): string {
  const base = entry.type === 'mask' ? 'mask' : describeAclPrincipal(entry, names);
  const scoped = entry.scope === 'default' ? `Default ${base}` : base;
  return scoped.charAt(0).toUpperCase() + scoped.slice(1);
}

export function aclChangeSentences(before: AclChangeSide, after: AclChangeSide, names: Record<string, string> = {}): AclChangeSentence[] {
  const lines: AclChangeSentence[] = [];
  if (after.owner !== undefined && after.owner.trim() !== before.owner) lines.push({ text: `Owner: ${before.owner} → ${after.owner.trim()}` });
  if (after.group !== undefined && after.group.trim() !== before.group) lines.push({ text: `Owning group: ${before.group} → ${after.group.trim()}` });
  const direct = new Map<string, { index: number; before: StorageAclPermissions; after: StorageAclPermissions }>();
  const diff = diffAcl(before.entries, after.entries);
  for (const e of diff.added) {
    direct.set(keyOf(e), { index: lines.length, before: NONE, after: e.permissions });
    lines.push({ text: `${subject(e, names)}: added with ${permissionWords(e.permissions)}` });
  }
  for (const e of diff.removed) {
    direct.set(keyOf(e), { index: lines.length, before: e.permissions, after: NONE });
    lines.push({ text: `${subject(e, names)}: removed (had ${permissionWords(e.permissions)})` });
  }
  for (const { before: was, after: now } of diff.changed) {
    direct.set(keyOf(now), { index: lines.length, before: was.permissions, after: now.permissions });
    lines.push({ text: `${subject(now, names)}: ${changeClause(was.permissions, now.permissions)} (was ${permissionWords(was.permissions)})` });
  }
  for (const change of diffEffective(before.entries, after.entries)) {
    const { suffix, widened } = direction(change.before, change.after);
    const own = direct.get(keyOf(change.entry));
    if (own && same(own.before, change.before) && same(own.after, change.after)) {
      lines[own.index] = { text: `${lines[own.index].text}${suffix}`, widened };
      continue;
    }
    const clause = change.change === 'removed' ? 'removed' : changeClause(change.before, change.after);
    lines.push({ text: `${subject(change.entry, names)}: effective access ${clause} (was ${permissionWords(change.before)})${change.change === 'removed' ? '.' : suffix}`, widened: change.change !== 'removed' && widened });
  }
  return lines;
}
